import { Fragment, useEffect, useMemo, useState } from 'react';
import { PUZZLES, applyAction, createGame, legalActions, type Action, type GameEvent, type GameState, type Puzzle } from '@ic/engine';
import { api } from '../lib/auth';
import { Board } from '../components/Board';
import { Confetti } from '../components/Confetti';
import { Icon } from '../components/Icons';
import { persisted, useStore } from '../lib/store';
import { morphTarget, useArrival, withTransition } from '../lib/transition';

const solvedStore = persisted<Record<string, boolean>>('ic.puzzles', {});
type Daily = Puzzle & { day: string };
/** Daily puzzles published by admins (cached for offline use). */
const dailyStore = persisted<Daily[]>('ic.dailyPuzzles', []);

export function Puzzles() {
  const solved = useStore(solvedStore);
  const daily = useStore(dailyStore);
  useEffect(() => { api<{ puzzles: Daily[] }>('/puzzles/daily').then((r) => dailyStore.set(r.puzzles)).catch(() => {}); }, []);
  const all: Puzzle[] = useMemo(() => [...daily, ...PUZZLES], [daily]);
  const today = new Date().toISOString().slice(0, 10);
  const [idx, setIdx] = useState(0);
  const puzzle = all[Math.min(idx, all.length - 1)];
  const start = useMemo(() => createGame(puzzle.ruleset), [puzzle]);
  const [state, setState] = useState<GameState>(start);
  const [last, setLast] = useState<{ action: Action; events: GameEvent[]; key: number } | null>(null);
  const [status, setStatus] = useState<'idle' | 'solved' | 'wrong'>('idle');
  const [hint, setHint] = useState(false);
  const arrival = useArrival();
  const morph = morphTarget(arrival === 'page');

  const load = (i: number) => withTransition(() => {
    setIdx(i);
    setState(createGame(all[i].ruleset));
    setLast(null);
    setStatus('idle');
    setHint(false);
  });

  const onAction = (a: Action) => {
    const r = applyAction(state, a);
    setState(r.state);
    setLast({ action: a, events: r.events, key: (last?.key ?? 0) + 1 });
    if (r.state.result?.winner === puzzle.toMove) {
      setStatus('solved');
      solvedStore.set({ ...solved, [puzzle.id]: true });
    } else if (!r.state.result && r.state.turn === puzzle.toMove) {
      // Several moves per turn: keep playing.
    } else setStatus('wrong');
  };

  return (
    <div className={`page puzzle-page ${arrival ? 'page-enter' : ''}`}>
      <Confetti fire={status === 'solved' ? `${puzzle.id}:${last?.key}` : null} />
      <div className="board-col">
        <Board
          state={state} orientation={puzzle.toMove} legal={status === 'idle' ? legalActions(state) : []}
          interactive={status === 'idle'} onAction={onAction}
          lastAction={last?.action} events={last?.events} eventsKey={last?.key}
        />
      </div>
      <aside className="side-panel">
        <h1 className={`panel-title ${morph.className}`} style={morph.style}>Puzzles</h1>
        <div className="card puzzle-card">
          <div className="muted small">{'day' in puzzle ? `Daily puzzle · ${(puzzle as Daily).day === today ? 'today' : (puzzle as Daily).day}` : `Puzzle ${idx - daily.length + 1} of ${PUZZLES.length}`} · {puzzle.toMove === 'w' ? 'White' : 'Black'} to move and win</div>
          <h2>{puzzle.title}</h2>
          {status === 'idle' && <p>{puzzle.ruleset.rules.movesPerTurn > 1 ? `You have ${state.actionsLeft} move${state.actionsLeft > 1 ? 's' : ''} this turn. Win before it ends.` : 'Find the move that wins immediately.'}</p>}
          {status === 'solved' && <p className="success">Solved! {state.result?.reason}.</p>}
          {status === 'wrong' && <p className="error">Not quite: that does not win this turn.</p>}
          {hint && <p className="muted small">Hint: {puzzle.hint}</p>}
          <div className="row gap wrap">
            {status !== 'idle' && <button className="btn secondary" onClick={() => load(idx)}>Retry</button>}
            {status === 'idle' && !hint && <button className="btn ghost" onClick={() => setHint(true)}>Hint</button>}
            <button className="btn primary" onClick={() => load((idx + 1) % all.length)}>Next puzzle</button>
          </div>
        </div>
        <div className="puzzle-list">
          {daily.length > 0 && <div className="puzzle-group">Daily puzzles</div>}
          {all.map((p, i) => (<Fragment key={p.id}>
            {i === daily.length && daily.length > 0 && <div className="puzzle-group">Classics</div>}
            <button className={`puzzle-item ${i === idx ? 'on' : ''}`} onClick={() => load(i)}>
              <span className={`state ${solved[p.id] ? 'done' : ''}`}>{solved[p.id] && <Icon name="check" size={12} />}</span> {p.title}
              {'day' in p && <span className="muted small puzzle-day">{(p as Daily).day === today ? 'today' : (p as Daily).day}</span>}
            </button>
          </Fragment>))}
        </div>
      </aside>
    </div>
  );
}
